import React from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

import selection from '@/assets/icomoon/admin/selection.json';

interface IcoMoonAttrs {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  strokeLinecap?: 'round' | 'butt' | 'square';
  strokeLinejoin?: 'round' | 'miter' | 'bevel';
}

interface IcoMoonIcon {
  icon: { paths: string[]; attrs?: IcoMoonAttrs[]; width?: number };
  properties: { name: string };
}

const ICON_SET_HEIGHT = selection.height;
const ICONS = new Map<string, IcoMoonIcon>(
  (selection.icons as IcoMoonIcon[]).map((i) => [i.properties.name, i]),
);

export type AdminIconName =
  | 'dashboard' | 'people' | 'folderOpen' | 'cardMembership' | 'email' | 'forum' | 'history'
  | 'reportProblem' | 'login' | 'devices' | 'vpnKey' | 'adminPanel' | 'insights' | 'search'
  | 'filter' | 'close' | 'refresh' | 'lightMode' | 'darkMode' | 'fileDownload' | 'add' | 'edit'
  | 'password' | 'lockOpen' | 'manageAccounts' | 'delete' | 'block' | 'logout' | 'send' | 'attach'
  | 'autorenew' | 'copy' | 'calendar' | 'chevronLeft' | 'chevronRight' | 'chevronDown' | 'arrowBack'
  | 'check' | 'trendingUp' | 'trendingDown' | 'visibility' | 'openInNew' | 'swap' | 'public'
  | 'person' | 'schedule' | 'sidebar' | 'menu' | 'storage' | 'cloud' | 'memory' | 'more'
  | 'lock' | 'gridView' | 'listView' | 'map' | 'upload' | 'sort' | 'star' | 'help' | 'home' | 'layers'
  | 'image' | 'building' | 'phone' | 'location' | 'arrowRight' | 'settings' | 'info' | 'apps' | 'tag' | 'link'
  | 'folder' | 'polygon' | 'line' | 'point' | 'raster' | 'visibilityOff' | 'zoomIn' | 'minus' | 'table' | 'drag'
  | 'ruler' | 'print' | 'crop' | 'target' | 'identify' | 'drawPoint' | 'drawLine' | 'drawPolygon' | 'circle'
  | 'freehand' | 'magnet' | 'undo' | 'redo' | 'save' | 'compass' | 'cube' | 'keyboard' | 'document' | 'palette'
  | 'label' | 'scale' | 'sql' | 'calc' | 'merge' | 'split' | 'flag' | 'folderPlus' | 'locate' | 'fullscreen'
  | 'basemap' | 'opacity' | 'chat' | 'grip';

export interface AdminIconProps {
  name: AdminIconName;
  size?: number;
  color?: string;
  sx?: SxProps<Theme>;
}

export const AdminIcon: React.FC<AdminIconProps> = ({ name, size = 20, color = 'currentColor', sx }) => {
  const entry = ICONS.get(name);
  if (!entry) return null;
  const width = entry.icon.width ?? ICON_SET_HEIGHT;
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${ICON_SET_HEIGHT}`}
      width={size}
      height={size}
      aria-hidden
      sx={{ flexShrink: 0, display: 'block', color, ...(sx as object) }}
    >
      {entry.icon.paths.map((d, i) => {
        const a = entry.icon.attrs?.[i] ?? {};
        return (
          <path
            key={i}
            d={d}
            fill={a.fill ?? 'currentColor'}
            stroke={a.stroke}
            strokeWidth={a.strokeWidth}
            strokeLinecap={a.strokeLinecap}
            strokeLinejoin={a.strokeLinejoin}
          />
        );
      })}
    </Box>
  );
};

export default AdminIcon;
